import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { brl, fdatetime, fdate } from "@/lib/format";
import StatCard from "@/components/StatCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Wallet, TrendingUp, TrendingDown, Receipt, Banknote, QrCode, CreditCard,
  AlertTriangle, CalendarClock, Settings2, CircleDot, Lock,
} from "lucide-react";

const PIE_COLORS = ["#10b981", "#0ea5e9", "#f59e0b", "#a1a1aa", "#f43f5e", "#8b5cf6"];

const tooltipStyle = {
  contentStyle: {
    background: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "0.75rem",
    fontSize: "12px",
    color: "hsl(var(--foreground))",
  },
  labelStyle: { color: "hsl(var(--foreground))" },
  itemStyle: { color: "hsl(var(--foreground))" },
};

function GoalDialog({ open, onOpenChange, current }) {
  const qc = useQueryClient();
  const [goal, setGoal] = useState(current || "");
  const save = async () => {
    try {
      await api.put("/settings", { monthly_goal: parseFloat(String(goal).replace(",", ".")) || 0 });
      toast.success("Meta atualizada");
      qc.invalidateQueries();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="goal-dialog">
        <DialogHeader>
          <DialogTitle>Meta mensal</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Label>Valor da meta (R$)</Label>
          <Input
            data-testid="goal-input"
            inputMode="decimal"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="15000"
          />
          <Button data-testid="goal-save" onClick={save} className="w-full">Salvar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [goalOpen, setGoalOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
    refetchInterval: 30000,
  });

  if (isLoading || !data) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  const pmCards = [
    { label: "Dinheiro", value: data.by_type.dinheiro, icon: Banknote },
    { label: "PIX", value: data.by_type.pix, icon: QrCode },
    { label: "Débito", value: data.by_type.debito, icon: CreditCard },
    { label: "Crédito", value: data.by_type.credito, icon: CreditCard },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Visão de hoje · {fdate(data.today)}</p>
        </div>
        <Badge
          data-testid="cash-status-badge"
          variant="outline"
          className={
            data.cash_open
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-500 px-3 py-1.5"
              : "border-red-500/40 bg-red-500/10 text-red-500 px-3 py-1.5"
          }
        >
          {data.cash_open ? <CircleDot className="mr-1.5 h-3.5 w-3.5" /> : <Lock className="mr-1.5 h-3.5 w-3.5" />}
          Caixa {data.cash_open ? "aberto" : "fechado"}
        </Badge>
      </div>

      {!data.cash_open && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4" data-testid="cash-closed-banner">
          <div className="text-sm">
            <span className="font-semibold text-amber-500">Caixa fechado.</span>{" "}
            <span className="text-muted-foreground">Abra o caixa para começar a registrar vendas.</span>
          </div>
          <Button asChild size="sm" className="bg-amber-500 text-black hover:bg-amber-400" data-testid="go-open-cash-btn">
            <Link to="/caixa">Abrir caixa</Link>
          </Button>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard testid="stat-saldo-atual" title="Saldo atual (caixa)" value={data.saldo_atual ?? data.saldo_inicial} icon={Wallet}
          sub={data.cash_open ? `Abertura: ${brl(data.saldo_inicial)}` : "Caixa fechado"} />
        <StatCard testid="stat-entradas" title="Entradas hoje" value={data.entradas_hoje} icon={TrendingUp} tone="success" />
        <StatCard testid="stat-saidas" title="Saídas hoje" value={data.saidas_hoje} icon={TrendingDown} tone="danger" />
        <StatCard testid="stat-ticket" title="Ticket médio" value={data.ticket_medio} icon={Receipt}
          sub={`${data.vendas_count} vendas · ${data.produtos_vendidos} produtos · ${data.planos_vendidos} planos`} />
      </div>

      <div className="grid gap-4 grid-cols-2 xl:grid-cols-4" data-testid="payment-breakdown">
        {pmCards.map((c) => (
          <div key={c.label} className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3" data-testid={`pm-${c.label.toLowerCase()}`}>
            <c.icon className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="text-xs text-muted-foreground">{c.label}</div>
              <div className="num font-semibold">{brl(c.value)}</div>
            </div>
          </div>
        ))}
      </div>

      {(user.role === "admin" || user.role === "gerente") && data.pendencias?.count > 0 && (
        <Card className="border-amber-500/30" data-testid="pending-card">
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" /> Pendências a receber
            </CardTitle>
            <Button asChild variant="outline" size="sm" data-testid="pending-goto-btn">
              <Link to="/movimentacoes">Quitar pendências</Link>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="num text-2xl font-bold text-amber-500" data-testid="pending-total">{brl(data.pendencias.total)}</div>
            <div className="text-xs text-muted-foreground mb-3">{data.pendencias.count} venda(s) com valor a receber</div>
            <div className="divide-y divide-border">
              {data.pendencias.sales.map((s) => (
                <div key={s.id} className="flex items-center justify-between py-2 text-sm" data-testid={`pending-row-${s.id}`}>
                  <div>
                    <span className="font-medium">{s.student_name || "Venda avulsa"}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{s.items.map((i) => i.name).join(", ")}</span>
                  </div>
                  <div className="text-right">
                    <span className="num font-semibold text-amber-500">falta {brl(s.pending_amount)}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{s.seller_name || "—"}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Vendas por hora (hoje)</CardTitle>
          </CardHeader>
          <CardContent className="h-64" data-testid="chart-hourly">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.by_hour} margin={{ left: -16, right: 8, top: 8 }}>
                <defs>
                  <linearGradient id="gHour" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="hour" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                <Tooltip {...tooltipStyle} formatter={(v) => brl(v)} />
                <Area type="monotone" dataKey="total" stroke="#10b981" strokeWidth={2} fill="url(#gHour)" name="Vendas" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-semibold">Meta mensal</CardTitle>
            {(user.role === "admin" || user.role === "gerente") && (
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setGoalOpen(true)} data-testid="edit-goal-btn">
                <Settings2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4" data-testid="goal-card">
            <div>
              <div className="num text-3xl font-bold">{brl(data.month_total)}</div>
              <div className="text-xs text-muted-foreground">
                de {brl(data.monthly_goal)} · mês anterior: {brl(data.prev_month_total)}
              </div>
            </div>
            <Progress value={Math.min(100, data.goal_percent)} className="h-2" />
            <div className="flex items-center justify-between text-sm">
              <span className="num font-semibold text-emerald-500" data-testid="goal-percent">{data.goal_percent}%</span>
              <span className="text-xs text-muted-foreground">atingido</span>
            </div>
            <div className="rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
              {data.month_total >= data.prev_month_total
                ? `+${brl(data.month_total - data.prev_month_total)} vs mês anterior`
                : `${brl(data.month_total - data.prev_month_total)} vs mês anterior`}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Últimos 7 dias</CardTitle>
          </CardHeader>
          <CardContent className="h-60" data-testid="chart-week">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.last7} margin={{ left: -16, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} />
                <Tooltip {...tooltipStyle} formatter={(v) => brl(v)} />
                <Bar dataKey="entradas" fill="#10b981" radius={[4, 4, 0, 0]} name="Entradas" />
                <Bar dataKey="saidas" fill="#ef4444" radius={[4, 4, 0, 0]} name="Saídas" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Por forma de pagamento</CardTitle>
          </CardHeader>
          <CardContent className="h-60" data-testid="chart-payment">
            {data.by_method.length === 0 ? (
              <div className="grid h-full place-items-center text-sm text-muted-foreground">Sem vendas hoje</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.by_method} dataKey="value" nameKey="name" innerRadius={52} outerRadius={80} paddingAngle={3}>
                    {data.by_method.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip {...tooltipStyle} formatter={(v) => brl(v)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Últimas vendas</CardTitle>
          </CardHeader>
          <CardContent data-testid="latest-sales">
            {data.ultimas_vendas.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Nenhuma venda ainda. Clique em <span className="font-semibold text-emerald-500">Nova Venda</span> para começar.
              </div>
            ) : (
              <div className="divide-y divide-border">
                {data.ultimas_vendas.map((s) => (
                  <div key={s.id} className="flex items-center justify-between py-2.5 text-sm" data-testid={`latest-sale-${s.id}`}>
                    <div>
                      <div className="font-medium">{s.student_name || "Venda avulsa"}</div>
                      <div className="text-xs text-muted-foreground">
                        {s.items.map((i) => `${i.qty > 1 ? i.qty + "x " : ""}${i.name}`).join(", ")}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="num font-semibold text-emerald-500">{brl(s.total)}</div>
                      <div className="text-xs text-muted-foreground">
                        {s.payment_method_name} · {fdatetime(s.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Alertas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3" data-testid="alerts-panel">
            {data.low_stock.length === 0 && data.due_students.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">Nenhum alerta no momento.</div>
            )}
            {data.low_stock.slice(0, 4).map((p) => (
              <div key={p.id} className="flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                <div>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">Estoque: {p.stock} (mín. {p.min_stock})</div>
                </div>
              </div>
            ))}
            {data.due_students.slice(0, 4).map((s) => (
              <div key={s.id} className="flex items-center gap-3 rounded-lg border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-sm">
                <CalendarClock className="h-4 w-4 text-sky-500 shrink-0" />
                <div>
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {s.plan_name} vence {fdate(s.due_date)}
                  </div>
                </div>
              </div>
            ))}
            <div className="pt-1 text-xs text-muted-foreground">
              Categorias hoje: {data.by_category.map((c) => `${c.name} (${brl(c.value)})`).join(" · ") || "—"}
            </div>
          </CardContent>
        </Card>
      </div>

      <GoalDialog open={goalOpen} onOpenChange={setGoalOpen} current={data.monthly_goal} />
    </div>
  );
}
